import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type Criticality = 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High';

/**
 * Normalize criticality value to consistent 5-level format (Very Low, Low, Medium, High, Very High)
 */
export function normalizeCriticality(value: string | undefined | null): Criticality | null {
  if (!value) return null;
  
  const normalized = value.toUpperCase().trim();
  
  // Map all variations to 5-level scale
  if (normalized === 'VL' || normalized === 'VERY LOW' || normalized === 'VERYLOW') return 'Very Low';
  if (normalized === 'L' || normalized === 'LOW') return 'Low';
  if (normalized === 'M' || normalized === 'MEDIUM') return 'Medium';
  if (normalized === 'H' || normalized === 'HIGH') return 'High';
  if (normalized === 'VH' || normalized === 'VERY HIGH' || normalized === 'VERYHIGH' || normalized === 'CRITICAL') return 'Very High';
  
  return null;
}

/**
 * Get color class for criticality badge (5-level scale)
 */
export function getCriticalityColor(criticality: string | undefined | null): string {
  const normalized = normalizeCriticality(criticality);
  
  switch (normalized) {
    case 'Very High':
      return 'bg-danger/10 text-danger border-danger/20';
    case 'High':
      return 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20';
    case 'Medium':
      return 'bg-warning/10 text-warning border-warning/20';
    case 'Low':
      return 'bg-success/10 text-success border-success/20';
    case 'Very Low':
      return 'bg-muted text-muted-foreground border-border';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

/**
 * Get risk level from score (5-level scale)
 */
export function getRiskLevelFromScore(score: number): { label: Criticality; color: string } {
  if (score >= 20) return { label: 'Very High', color: 'bg-danger text-danger-foreground' };
  if (score >= 15) return { label: 'High', color: 'bg-orange-500 text-white' };
  if (score >= 10) return { label: 'Medium', color: 'bg-warning text-warning-foreground' };
  if (score >= 5) return { label: 'Low', color: 'bg-success text-success-foreground' };
  return { label: 'Very Low', color: 'bg-muted text-muted-foreground' };
}

/**
 * Get unified domain color class (for IT, OT, Cloud, DMZ, External)
 */
export function getDomainColor(domain?: string): string {
  switch (domain) {
    case 'IT':
      return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
    case 'OT':
      return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20';
    case 'Cloud':
      return 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20';
    case 'DMZ':
      return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
    case 'External':
      return 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

/**
 * Get domain icon color class (for lucide icons)
 */
export function getDomainIconColor(domain?: string): string {
  switch (domain) {
    case 'IT':
      return 'text-blue-600 dark:text-blue-400';
    case 'OT':
      return 'text-orange-600 dark:text-orange-400';
    case 'Cloud':
      return 'text-purple-600 dark:text-purple-400';
    case 'DMZ':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'External':
      return 'text-gray-600 dark:text-gray-400';
    default:
      return 'text-muted-foreground';
  }
}
